import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { CheckCircle2, Circle, PartyPopper, X } from "lucide-react";
import { useState } from "react";
import { authClient, useAuth } from "@/auth";
import { Button } from "@/components/catalyst/Button";
import { orpc } from "@/orpc/client";
import { useOnboardingChecklistItems } from "../hooks/use-onboarding-checklist-items";

/**
 * Onboarding checklist card shown on the dashboard for new users.
 * Checks the session flag first to avoid an API call for users
 * who have already dismissed onboarding.
 */
export function OnboardingChecklist() {
	const authSession = useAuth();

	// Skip API call entirely if user already completed onboarding
	if (authSession?.user?.completedOnboardingAt) {
		return null;
	}

	return <OnboardingChecklistInner />;
}

/**
 * Inner component that fetches checklist data and renders the card.
 * Only mounted when the user hasn't completed onboarding yet.
 */
function OnboardingChecklistInner() {
	const navigate = useNavigate();
	const router = useRouter();
	const [dismissed, setDismissed] = useState(false);

	const { data } = useSuspenseQuery(orpc.onboarding.checklist.queryOptions({}));

	const completeOnboarding = async () => {
		setDismissed(true);
		await authClient.updateUser({ completedOnboardingAt: new Date() });
		await router.invalidate();
	};

	const items = useOnboardingChecklistItems(data);
	const completedCount = items.filter((i) => i.completed).length;
	const totalCount = items.length;
	const allDone = completedCount === totalCount;

	const progressPercent = Math.round((completedCount / totalCount) * 100);

	if (dismissed) {
		return null;
	}

	return (
		<div className="rounded-xl border-2 border-accent bg-base-100 p-6">
			{/* Header */}
			<div className="mb-4 flex items-center justify-between">
				<div>
					<h3 className="text-lg font-semibold text-base-content">
						{allDone ? "You're all set!" : "Getting Started"}
					</h3>
					{!allDone && (
						<p className="mt-1 text-sm text-base-content">
							Complete these steps to start paying partners automatically and
							compliantly.
						</p>
					)}
					<p className="mt-2 text-sm text-base-content/60">
						{allDone
							? "You've completed all the steps to get started."
							: `${completedCount} of ${totalCount} steps completed`}
					</p>
				</div>
				{!allDone && (
					<Button
						plain
						onClick={() => completeOnboarding()}
						aria-label="Dismiss onboarding checklist"
					>
						<X className="size-4" />
					</Button>
				)}
			</div>

			{/* Progress bar */}
			<div className="mb-5 h-1.5 w-full overflow-hidden rounded-full bg-base-content/10">
				<div
					className={`h-full rounded-full transition-all duration-300 ${allDone ? "bg-success" : "bg-primary"}`}
					style={{ width: `${progressPercent}%` }}
				/>
			</div>

			{/* Checklist items */}
			<div className="space-y-1">
				{items.map((item) => (
					<button
						key={item.key}
						type="button"
						onClick={() =>
							typeof item.action === "function"
								? item.action()
								: navigate(item.action)
						}
						disabled={allDone}
						className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors cursor-pointer ${allDone ? "cursor-default" : "hover:bg-base-content/5"}`}
					>
						{item.completed ? (
							<CheckCircle2 className="size-5 shrink-0 text-success" />
						) : (
							<Circle className="size-5 shrink-0 text-base-content/25" />
						)}
						<span
							className={
								item.completed
									? "text-sm text-base-content/40 line-through"
									: "text-sm text-base-content"
							}
						>
							{item.label}
						</span>
					</button>
				))}
			</div>

			{/* Completion footer */}
			{allDone && (
				<div className="mt-5 flex items-center justify-between border-t border-base-content/10 pt-4">
					<div className="flex items-center gap-2 text-sm text-success">
						<PartyPopper className="size-4" />
						<span>
							Your setup is complete. The referral program activates once
							partners approve the terms.
						</span>
					</div>
					<Button outline onClick={() => completeOnboarding()}>
						Understood
					</Button>
				</div>
			)}
		</div>
	);
}
